// src/components/strategies/EquityChart.jsx
import { useMemo } from 'react';
import Chart from 'react-apexcharts';
import { useTranslation } from 'react-i18next';

const normalizeTime = (time) => {
  if (!time) return Date.now();
  if (typeof time === 'string' && time.length > 13) return parseInt(time.slice(0, 13), 10);
  return new Date(time).getTime();
};

const EquityChart = ({ completedTrades, initialBalance = 0, minimal = false, cardHeight = null }) => {
  const { t } = useTranslation();
  const seriesData = useMemo(() => {
    const trades = completedTrades || [];
    if (!Array.isArray(trades) || trades.length === 0) {
      return [{ name: 'Balance', data: [{ x: Date.now(), y: initialBalance }] }];
    }
    const sortedTrades = [...trades].sort((a, b) => normalizeTime(a.exit_time) - normalizeTime(b.exit_time));
    let currentBalance = initialBalance;
    const dataPoints = sortedTrades.map(trade => {
      let pnlValue = 0;
      if (typeof trade.net_pnl_usdt === 'number') pnlValue = trade.net_pnl_usdt;
      else if (trade["Net PnL, $"]) pnlValue = parseFloat(String(trade["Net PnL, $"]).replace('$', '').replace(',', '')) || 0;
      currentBalance += pnlValue;
      return { x: normalizeTime(trade.exit_time), y: currentBalance };
    });
    const firstTime = normalizeTime(sortedTrades[0].entry_time);
    return [{ name: 'Balance', data: [{ x: firstTime, y: initialBalance }, ...dataPoints] }];
  }, [completedTrades, initialBalance]);

  const chartOptions = {
    theme: { mode: 'dark' },
    chart: { type: 'area', background: 'transparent', toolbar: { show: false }, sparkline: { enabled: true }, animations: { enabled: false } },
    stroke: { width: 2, curve: 'smooth' },
    colors: [seriesData[0].data.slice(-1)[0]?.y >= initialBalance ? '#34D399' : '#F43F5E'],
    fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.1, stops: [0, 100] } },
    xaxis: { type: 'datetime', tooltip: { enabled: false } },
    tooltip: { theme: 'dark', x: { format: 'dd MMM HH:mm' }, y: { formatter: (val) => `$${val.toFixed(2)}` } },
    grid: { padding: { top: 5, bottom: 5, left: 0, right: 0 } } 
  };

  return minimal ? (
    <div className="w-full h-full min-h-[100px]"><Chart options={chartOptions} series={seriesData} type="area" width="100%" height="100%" /></div>
  ) : (
    <div
      className="bg-slate-800 p-6 rounded-xl shadow-lg flex flex-col"
      style={cardHeight ? { height: `${cardHeight}px` } : undefined}
    >
      <h3 className="text-xl font-semibold text-white mb-4">{t('equity.title')}</h3>
      <div className="flex-1 min-h-[160px]"><Chart options={chartOptions} series={seriesData} type="area" width="100%" height="100%" /></div>
    </div>
  );
};

export default EquityChart;
